import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import type { Task } from 'ember-concurrency';
import { eq } from 'ember-truth-helpers';
import LoadMore from 'garaje/components/load-more';
import SelectAllCheckbox from 'garaje/components/select-all-checkbox';
import SortableHeaderColumn from 'garaje/components/sortable-header-column';
import VisitorsApprovalsTableRow from 'garaje/components/visitors/approvals-table/row';
import type { InviteWithVisitApproval } from 'garaje/models/invite';
import { TrackedSet } from 'tracked-built-ins';

interface VisitorsApprovalsTableSignature {
  Args: {
    approveInviteTask: Task<void, [InviteWithVisitApproval]>;
    bulkApproveInvitesTask: Task<InviteWithVisitApproval[], [InviteWithVisitApproval[]]>;
    bulkDenyInvitesTask: Task<InviteWithVisitApproval[], [InviteWithVisitApproval[]]>;
    columns: string[];
    denyInviteTask: Task<void, [InviteWithVisitApproval]>;
    hasMoreData: boolean;
    invites: InviteWithVisitApproval[];
    sortBy: string;
    sortDirection: 'asc' | 'desc';

    changeSort(field: string, direction: string): unknown;
    loadMoreData(): void;
  };
}

export default class VisitorsApprovalsTable extends Component<VisitorsApprovalsTableSignature> {
  @service declare abilities: AbilitiesService;

  @tracked selectedInvites: TrackedSet<InviteWithVisitApproval> = new TrackedSet();

  /**
   * It's possible for an invite to require approval, but the current user isn't able to approve.
   * For example, if the current user is a Location Admin and not named in the blocklist contacts,
   * they won't be able to review an invite that matched a blocklist rule.
   *
   * This getter filters the list of approval-required invites to the ones that are actionable by the
   * current user. It's used to determine whether a particular invite is reviewable, or if all reviewable
   * invites are selected, but not for determining which invites to display (which should be all, including
   * ones that aren't actionable).
   */
  get reviewableInvites(): TrackedSet<InviteWithVisitApproval> {
    const reviewableInvites = this.args.invites.filter((invite: InviteWithVisitApproval) => {
      return this.abilities.can('review entry-approval', {
        visitApproval: invite.visitApproval,
        context: 'location',
        parent: invite,
      });
    });
    return new TrackedSet(reviewableInvites);
  }

  get showBulkReviewButtons(): boolean {
    return this.selectedInvites.size > 0;
  }

  get bulkReviewButtonsDisabled(): boolean {
    return !this.showBulkReviewButtons || this.bulkApproveInvitesTask.isRunning || this.bulkDenyInvitesTask.isRunning;
  }

  bulkApproveInvitesTask = dropTask(async (): Promise<void> => {
    const invitesToRemove = await this.args.bulkApproveInvitesTask.perform([...this.selectedInvites]);
    invitesToRemove.forEach((invite) => this.selectedInvites.delete(invite));
  });

  bulkDenyInvitesTask = dropTask(async (): Promise<void> => {
    const invitesToRemove = await this.args.bulkDenyInvitesTask.perform([...this.selectedInvites]);
    invitesToRemove.forEach((invite) => this.selectedInvites.delete(invite));
  });

  @action
  selectOrDeselectAllInvites(): void {
    if (this.selectedInvites.size < this.reviewableInvites.size) {
      // not all invites are selected; select all (from the ones the current user can review)
      this.selectedInvites = new TrackedSet(this.reviewableInvites);
    } else {
      this.selectedInvites.clear();
    }
  }

  @action
  selectOrDeselectInvite(invite: InviteWithVisitApproval): void {
    if (this.selectedInvites.has(invite)) {
      this.selectedInvites.delete(invite);
    } else {
      this.selectedInvites.add(invite);
    }
  }

  // local helpers
  @action
  isInviteSelected(invite: InviteWithVisitApproval): boolean {
    return this.selectedInvites.has(invite);
  }

  @action
  showColumn(columnName: string): boolean {
    return this.args.columns.includes(columnName);
  }

  <template>
    <table data-test-approvals-table>
      <thead>
        <tr>
          <th class="align-middle border-b">
            <SelectAllCheckbox
              @selectedItemCount={{this.selectedInvites.size}}
              @totalItemCount={{@invites.length}}
              data-test-select-all-invites
              {{on "change" this.selectOrDeselectAllInvites}}
            />
          </th>

          <th class="align-middle border-b min-w-[8rem]" data-test-due-at-header>
            <SortableHeaderColumn
              @name="Due at"
              @isActive={{eq @sortBy "dueAt"}}
              @sortDirection={{@sortDirection}}
              @sortField="dueAt"
              @sortAction={{@changeSort}}
            />
          </th>

          {{#if (this.showColumn "name")}}
            <th class="align-middle border-b min-w-[8rem]" data-test-visitor-name-header>
              <SortableHeaderColumn
                @name="Visitor name"
                @isActive={{eq @sortBy "name"}}
                @sortDirection={{@sortDirection}}
                @sortField="name"
                @sortAction={{@changeSort}}
              />
            </th>
          {{/if}}

          {{#if (this.showColumn "entryStatus")}}
            <th class="align-middle border-b text-sm text-carbon-50 min-w-[8rem]" data-test-entry-status-header>
              Status
            </th>
          {{/if}}

          {{#if (this.showColumn "type")}}
            <th class="align-middle border-b min-w-[8rem]" data-test-visitor-type-header>
              <SortableHeaderColumn
                @name="Visitor type"
                @isActive={{eq @sortBy "type"}}
                @sortDirection={{@sortDirection}}
                @sortField="type"
                @sortAction={{@changeSort}}
              />
            </th>
          {{/if}}

          {{#if (this.showColumn "approvalType")}}
            <th class="align-middle border-b text-sm text-carbon-50 min-w-[8rem]" data-test-approval-type-header>
              Approval type
            </th>
          {{/if}}

          {{#if (this.showColumn "returningVisitor")}}
            <th class="align-middle border-b text-sm text-carbon-50" data-test-returning-visitor-header>
              Returning visitor
            </th>
          {{/if}}

          {{#if (this.showColumn "privateNotes")}}
            <th class="align-middle border-b text-sm text-carbon-50 min-w-[12rem]" data-test-private-notes-header>
              Private notes
            </th>
          {{/if}}

          <th class="align-middle border-b whitespace-nowrap">
            <button
              data-test-bulk-approve-button
              type="button"
              class="btn btn--outline btn--small mr-4 w-[8rem] {{unless this.showBulkReviewButtons 'invisible'}}"
              disabled={{this.bulkReviewButtonsDisabled}}
              {{on "click" this.bulkApproveInvitesTask.perform}}
            >
              <span class="icon-check-green mr-1"></span>
              Approve all
            </button>
            <button
              data-test-bulk-deny-button
              type="button"
              class="btn btn--outline btn--small ml-2 w-[8rem] {{unless this.showBulkReviewButtons 'invisible'}}"
              disabled={{this.bulkReviewButtonsDisabled}}
              {{on "click" this.bulkDenyInvitesTask.perform}}
            >
              <span class="icon-cross-red mr-1"></span>
              Deny all
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        {{#each @invites as |invite|}}
          <VisitorsApprovalsTableRow
            @approveInviteTask={{@approveInviteTask}}
            @columns={{@columns}}
            @denyInviteTask={{@denyInviteTask}}
            @hideReviewButtons={{this.showBulkReviewButtons}}
            @invite={{invite}}
            @isSelected={{this.isInviteSelected invite}}
            @selectOrDeselectInvite={{this.selectOrDeselectInvite}}
          />
        {{/each}}

        <tr>
          <td colspan="6">
            <LoadMore
              class="load-more m-4"
              @hasMorePages={{@hasMoreData}}
              @useWindow={{true}}
              @action={{@loadMoreData}}
            />
          </td>
        </tr>
      </tbody>
    </table>
  </template>
}
